import { useCallback, useEffect, useRef, useState } from "react";
import { Flame, Rocket, ShieldCheck, Wallet, Zap } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";

const DEFAULT_SETTINGS = {
  enabled: true,
  min_bet: 1,
  max_bet: 20,
  min_cashout_multiplier: 1.1,
  max_cashout_multiplier: 10,
  rules_text: "Bet from your wallet and tap Cashout before the rocket crashes. If it crashes first, the bet is lost.",
};

function money(value) {
  return `$${Number(value || 0).toFixed(2)}`;
}

function multiplierText(value) {
  return `${Number(value || 1).toFixed(2)}x`;
}

function readServerTime(value) {
  const parsed = Date.parse(value || "");
  return Number.isFinite(parsed) ? parsed : Date.now();
}

function computeMultiplier(session) {
  if (!session) return 1;
  const started = readServerTime(session.started_at);
  const server = readServerTime(session.server_time);
  const localElapsed = Math.max(0, Date.now() - server);
  const elapsedSeconds = Math.max(0, (server + localElapsed - started) / 1000);
  const growth = Number(session.growth_per_second || 0.42);
  return Math.max(1, 1 + elapsedSeconds * growth);
}

export default function CrashHomeGame({ dark = true, compact = false }) {
  const { user, refreshUser } = useAuth() || {};
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [walletBalance, setWalletBalance] = useState(null);
  const [betAmount, setBetAmount] = useState("1");
  const [session, setSession] = useState(null);
  const [starting, setStarting] = useState(false);
  const [cashingOut, setCashingOut] = useState(false);
  const [shownMultiplier, setShownMultiplier] = useState(1);
  const [lastResult, setLastResult] = useState(null);
  const [history, setHistory] = useState([]);
  const rafRef = useRef(null);
  const pollRef = useRef(null);

  const minBet = Number(settings.min_bet || 1);
  const maxBet = Number(settings.max_bet || minBet);
  const minCashout = Number(settings.min_cashout_multiplier || 1.1);
  const maxCashout = Number(settings.max_cashout_multiplier || 10);
  const currentBet = Number(betAmount || 0);
  const playing = Boolean(session?.id);

  const stopTimers = useCallback(() => {
    if (rafRef.current) {
      window.cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (pollRef.current) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const rememberResult = useCallback((result) => {
    if (!result) return;
    setLastResult(result);
    setHistory((current) => [result, ...current].slice(0, 4));
  }, []);

  const loadCrash = useCallback(async () => {
    if (!user || user.role !== "user") {
      setSettings(DEFAULT_SETTINGS);
      setWalletBalance(null);
      setSession(null);
      return;
    }
    try {
      const { data } = await api.get("/me/crash");
      const nextSettings = { ...DEFAULT_SETTINGS, ...(data.settings || {}) };
      setSettings(nextSettings);
      setWalletBalance(Number(data.wallet_balance || 0));
      setBetAmount((value) => (Number(value || 0) > 0 ? value : String(nextSettings.min_bet || 1)));
      setSession(data.session || null);
    } catch {
      setSettings(DEFAULT_SETTINGS);
    }
  }, [user]);

  const checkSession = useCallback(async ({ quiet = true } = {}) => {
    if (!user || user.role !== "user") return;
    try {
      const { data } = await api.get("/me/crash/session");
      setWalletBalance(Number(data.wallet_balance || 0));
      if (data.active && data.session) {
        setSession((current) => (current?.id === data.session.id ? current : data.session));
        return;
      }
      setSession(null);
      if (data.result) {
        rememberResult(data.result);
        if (!data.result.won && !quiet) toast.error(`Crashed at ${multiplierText(data.result.crash_point)}.`);
      }
    } catch {
      if (!quiet) toast.error("Crash session could not refresh.");
    }
  }, [rememberResult, user]);

  useEffect(() => {
    loadCrash();
  }, [loadCrash]);

  useEffect(() => {
    stopTimers();
    if (!session?.id) return undefined;

    const tick = () => {
      setShownMultiplier((current) => {
        const next = computeMultiplier(session);
        return next > current ? next : current;
      });
      rafRef.current = window.requestAnimationFrame(tick);
    };
    setShownMultiplier((current) => Math.max(current || 1, computeMultiplier(session)));
    rafRef.current = window.requestAnimationFrame(tick);
    pollRef.current = window.setInterval(() => {
      checkSession({ quiet: true });
    }, 700);

    return stopTimers;
  }, [checkSession, session, stopTimers]);

  useEffect(() => () => stopTimers(), [stopTimers]);

  const startCrash = async () => {
    if (playing || starting) return;
    if (!user || user.role !== "user") {
      toast.error("Please sign in to play Crash.");
      return;
    }
    if (!settings.enabled) {
      toast.error("Crash is turned off right now.");
      return;
    }
    if (!Number.isFinite(currentBet) || currentBet <= 0) {
      toast.error("Enter a valid bet amount.");
      return;
    }

    setStarting(true);
    setLastResult(null);
    setShownMultiplier(1);
    try {
      const { data } = await api.post("/me/crash/start", { amount: currentBet });
      setWalletBalance(Number(data.wallet_balance || data.result?.wallet_balance || 0));
      if (data.user) await refreshUser?.({ silent: true });
      if (data.active && data.session) {
        setSession(data.session);
        toast.success("Crash started. Tap Cashout before it crashes.");
      } else {
        setSession(null);
        rememberResult(data.result);
      }
    } catch (err) {
      toast.error(formatApiError(err, { audience: "player" }));
      setSession(null);
      setShownMultiplier(1);
    } finally {
      setStarting(false);
    }
  };

  const cashoutCrash = async () => {
    if (!session?.id || cashingOut) return;
    setCashingOut(true);
    try {
      const { data } = await api.post("/me/crash/cashout", { session_id: session.id });
      setSession(null);
      stopTimers();
      setWalletBalance(Number(data.result?.wallet_balance || 0));
      if (data.user) await refreshUser?.({ silent: true });
      rememberResult(data.result);
      if (data.result?.won) {
        setShownMultiplier(Number(data.result.cashout_multiplier || shownMultiplier));
        toast.success(`Cashed out ${money(data.result.payout_amount)} at ${multiplierText(data.result.cashout_multiplier)}.`);
      } else {
        setShownMultiplier(Number(data.result?.crash_point || shownMultiplier));
        toast.error(`Crashed at ${multiplierText(data.result?.crash_point)}.`);
      }
    } catch (err) {
      toast.error(formatApiError(err, { audience: "player" }));
      await checkSession({ quiet: false });
    } finally {
      setCashingOut(false);
    }
  };

  const quickBets = [1, 2, 5, 10].filter((value) => value >= minBet && value <= maxBet);
  const displayMultiplier = playing ? shownMultiplier : Number(lastResult?.cashout_multiplier || lastResult?.target_multiplier || lastResult?.crash_point || shownMultiplier || 1);
  const flightMultiplier = Number(displayMultiplier || 1);
  const flightLeft = playing
    ? Math.min(86, 10 + (flightMultiplier - 1) * 9)
    : lastResult?.won
      ? 78
      : lastResult
        ? 58
        : 14;
  const flightBottom = playing
    ? Math.min(76, 34 + (flightMultiplier - 1) * 7)
    : lastResult?.won
      ? 70
      : lastResult
        ? 56
        : 34;
  const flightAngle = playing || lastResult?.won ? -24 : lastResult ? 14 : -12;
  const flightProgress = Math.min(94, Math.max(10, flightLeft + 4));
  const crashed = Boolean(lastResult && !lastResult.won && !playing);

  return (
    <section
      className={`relative overflow-hidden rounded-3xl border shadow-xl ${
        dark
          ? "border-orange-400/20 bg-[#080D16] text-white shadow-black/20"
          : "border-orange-200 bg-white text-slate-950 shadow-slate-200/80"
      } ${compact ? "p-4 sm:p-5" : "p-5 sm:p-6 md:p-7"}`}
      data-testid="crash-home-game"
    >
      <div className="pointer-events-none absolute -right-16 -top-20 h-52 w-52 rounded-full bg-orange-500/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-20 h-52 w-52 rounded-full bg-[#2F6FA5]/20 blur-3xl" />

      <div className="relative grid gap-5 lg:grid-cols-[1fr_1.05fr] lg:items-center">
        <div className="order-2 flex flex-col lg:order-1">
          <div className="order-2 mb-2 mt-4 inline-flex items-center gap-2 self-start rounded-full border border-orange-400/30 bg-orange-500/10 px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-orange-200 lg:order-none lg:mt-0">
            <Flame className="h-3.5 w-3.5" />
            Wallet Crash
          </div>
          <h2 className="order-2 font-display text-3xl font-black tracking-tight sm:text-4xl lg:order-none">Crash Run</h2>
          <p className={`order-2 mt-2 max-w-xl text-sm leading-relaxed lg:order-none ${dark ? "text-slate-300" : "text-slate-600"}`}>
            {settings.rules_text || DEFAULT_SETTINGS.rules_text}
          </p>

          <div className="order-1 grid gap-3 lg:order-none lg:mt-4">
            <label className="block">
              <span className="mb-1 block text-[10px] font-black uppercase tracking-[0.18em] text-[#8FC5F3]">Bet amount</span>
              <input
                value={betAmount}
                onChange={(event) => setBetAmount(event.target.value)}
                type="number"
                min={minBet}
                max={maxBet}
                step="0.01"
                disabled={playing || starting}
                className={`w-full rounded-2xl border px-4 py-3 font-black outline-none disabled:cursor-not-allowed disabled:opacity-60 ${
                  dark ? "border-white/10 bg-black/25 text-white" : "border-slate-200 bg-white text-slate-950"
                }`}
              />
            </label>
          </div>

          <div className="order-1 mt-3 flex flex-wrap gap-2 lg:order-none">
            {quickBets.map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setBetAmount(String(value))}
                disabled={playing || starting}
                className={`rounded-full border px-3 py-1.5 text-xs font-black disabled:opacity-50 ${
                  dark ? "border-white/10 bg-white/5 text-slate-200" : "border-slate-200 bg-slate-50 text-slate-700"
                }`}
              >
                {money(value)}
              </button>
            ))}
          </div>

          <div className="order-1 mt-3 flex flex-col gap-2 sm:flex-row lg:order-none lg:mt-4">
            {!playing ? (
              <button
                type="button"
                onClick={startCrash}
                disabled={starting || !settings.enabled}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-orange-500 to-[#2F6FA5] px-6 py-3 font-black text-white shadow-lg shadow-orange-500/20 transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                <Rocket className="h-4 w-4" />
                {starting ? "Starting..." : `Start Crash for ${money(currentBet || minBet)}`}
              </button>
            ) : (
              <button
                type="button"
                onClick={cashoutCrash}
                disabled={cashingOut}
                className="inline-flex w-full animate-pulse items-center justify-center gap-2 rounded-full bg-gradient-to-r from-emerald-400 to-cyan-500 px-6 py-3 font-black text-[#041019] shadow-lg shadow-emerald-500/20 transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                <Zap className="h-4 w-4" />
                {cashingOut ? "Cashing out..." : `Cashout Crash at ${multiplierText(shownMultiplier)}`}
              </button>
            )}
          </div>

          <div className="order-1 mt-3 flex flex-wrap items-center gap-2 text-xs lg:order-none">
            <span className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 ${dark ? "border-white/10 bg-white/5 text-slate-300" : "border-slate-200 bg-slate-50 text-slate-600"}`}>
              <Wallet className="h-3.5 w-3.5" />
              Wallet: {walletBalance == null ? (user?.coins != null ? money(Number(user.coins || 0) / 1000) : "sign in") : money(walletBalance)}
            </span>
            <span className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 ${dark ? "border-white/10 bg-white/5 text-slate-300" : "border-slate-200 bg-slate-50 text-slate-600"}`}>
              <ShieldCheck className="h-3.5 w-3.5" />
              {money(minBet)}-{money(maxBet)} / cashout {multiplierText(minCashout)}-{multiplierText(maxCashout)}
            </span>
          </div>
        </div>

        <div
          className={`order-1 relative min-h-[245px] overflow-hidden rounded-3xl border p-3 lg:order-2 lg:min-h-[300px] lg:p-5 ${
            dark ? "border-white/10 bg-[#030913]" : "border-slate-200 bg-[#071421]"
          }`}
          aria-label="Crash airplane flight"
        >
          <div className="absolute inset-x-8 bottom-10 h-1 rounded-full bg-gradient-to-r from-transparent via-[#2F6FA5] to-transparent opacity-60" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_20%,rgba(249,115,22,0.24),transparent_34%),radial-gradient(circle_at_50%_70%,rgba(47,111,165,0.26),transparent_42%)]" />
          <svg
            className="pointer-events-none absolute inset-0 h-full w-full"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path
              d="M8 82 C24 76 34 64 45 53 C58 40 70 25 90 14"
              fill="none"
              stroke="rgba(143,197,243,0.16)"
              strokeWidth="1.6"
            />
            <path
              d="M8 82 C24 76 34 64 45 53 C58 40 70 25 90 14"
              fill="none"
              stroke="rgba(251,146,60,0.72)"
              strokeDasharray={`${flightProgress} 120`}
              strokeLinecap="round"
              strokeWidth="1.25"
            />
          </svg>
          <div className="pointer-events-none absolute inset-x-10 bottom-8 h-16 rounded-[50%] border-t border-[#2F6FA5]/40" />
          <div
            className={`absolute z-10 ${
              playing ? "" : "transition-[left,bottom,transform] duration-500 ease-out"
            } ${
              playing ? "drop-shadow-[0_0_22px_rgba(251,146,60,0.75)]" : ""
            }`}
            style={{
              left: `${flightLeft}%`,
              bottom: `${flightBottom}%`,
              transform: `translate(-50%, 50%) rotate(${flightAngle}deg)`,
            }}
          >
            {(playing || lastResult?.won) && !crashed && (
              <span className="absolute left-[-62px] top-1/2 h-4 w-20 -translate-y-1/2 rotate-6 rounded-full bg-gradient-to-l from-orange-300 via-orange-400/70 to-transparent blur-md" />
            )}
            <div className="relative h-16 w-20 sm:h-20 sm:w-24">
              {crashed && (
                <>
                  <span className="absolute left-0 top-0 text-5xl animate-pulse drop-shadow-[0_0_22px_rgba(248,113,113,0.95)] sm:text-6xl">{"\u{1F525}"}</span>
                  <span className="absolute left-8 top-4 text-4xl animate-pulse drop-shadow-[0_0_18px_rgba(251,146,60,0.9)] sm:left-9 sm:top-5 sm:text-5xl">{"\u{1F4A5}"}</span>
                  <span className="absolute -left-8 top-8 h-4 w-24 rotate-12 rounded-full bg-gradient-to-l from-red-500 via-orange-400 to-transparent blur-md" />
                </>
              )}
              <span
                className={`relative block text-5xl leading-none drop-shadow-[0_0_18px_rgba(251,191,36,0.75)] sm:text-6xl ${
                  crashed ? "opacity-40 saturate-50" : ""
                }`}
                aria-hidden="true"
              >
                {"\u2708\uFE0F"}
              </span>
              {playing && (
                <span className="absolute -inset-2 rounded-full border border-orange-300/25 animate-ping" />
              )}
            </div>
          </div>
          <div className="relative flex min-h-[210px] flex-col items-center justify-end pb-5 text-center lg:min-h-[260px] lg:pb-8">
            <div className={`rounded-3xl border border-white/10 bg-[#030913]/70 px-6 py-2 font-display text-5xl font-black tabular-nums shadow-2xl backdrop-blur sm:text-6xl lg:px-7 lg:py-3 ${lastResult?.won && !playing ? "text-emerald-300" : lastResult && !playing ? "text-red-300" : "text-white"}`}>
              {multiplierText(displayMultiplier)}
            </div>
            <div className="mt-3 rounded-full bg-[#030913]/70 px-4 py-1.5 text-sm font-bold text-slate-300 backdrop-blur">
              {playing ? "Plane is flying - tap Cashout Crash before it crashes" : lastResult ? (lastResult.won ? `Cashed out: ${money(lastResult.payout_amount)}` : `Plane crashed at ${multiplierText(lastResult.crash_point)}`) : "Ready to launch"}
            </div>
          </div>
        </div>
      </div>

      {(lastResult || history.length > 0) && (
        <div className="relative mt-5 grid gap-2 sm:grid-cols-4">
          {(history.length ? history : [lastResult]).filter(Boolean).map((row, index) => (
            <div
              key={`${row.crash_point}-${row.cashout_multiplier || row.target_multiplier || "lost"}-${index}`}
              className={`rounded-2xl border px-4 py-3 text-xs ${
                row.won
                  ? "border-emerald-400/20 bg-emerald-500/10 text-emerald-100"
                  : "border-red-400/20 bg-red-500/10 text-red-100"
              }`}
            >
              <div className="font-black">{row.won ? "Cashout" : "Crash"}</div>
              <div className="mt-1 text-slate-300">
                {row.won ? `Cashout ${multiplierText(row.cashout_multiplier || row.target_multiplier)}` : `Crash ${multiplierText(row.crash_point)}`}
              </div>
              <div className="mt-1 font-black">{row.won ? "+" : "-"}{money(row.net_amount)}</div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
